import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

interface InitialFilters {
  serviceCategory?: string;
  effectiveDomain?: string;
  serviceSubcategory?: string;
  genders?: string[];
  language?: string;
}

const FILTERS_STORAGE_KEY = 'practitioner_filters';
const FILTERS_EXPIRY_DAYS = 30;

// Helper to check if stored filters are expired
const isFiltersExpired = (timestamp: number): boolean => {
  const now = Date.now();
  const expiryMs = FILTERS_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  return now - timestamp > expiryMs;
};

// Helper to get stored filters from localStorage
const getStoredFilters = () => {
  try {
    const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    if (isFiltersExpired(parsed.timestamp)) {
      localStorage.removeItem(FILTERS_STORAGE_KEY);
      return null;
    }
    
    return parsed.filters;
  } catch (error) {
    console.error('Error reading stored filters:', error);
    return null;
  }
};

export const usePractitionersFilters = (initialFilters?: InitialFilters) => {
  const [searchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState("rating");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  
  // Priority: initialFilters > localStorage > searchParams > defaults
  const rawCategory = searchParams.get("category");
  const normalizedCategory = rawCategory === 'all' ? 'all_types' : rawCategory;
  
  const storedFilters = !initialFilters ? getStoredFilters() : null;
  
  const initialCategory = initialFilters?.serviceCategory || 
                          storedFilters?.serviceCategories?.[0] ||
                          normalizedCategory || 
                          'all_types';
  
  const [filters, setFilters] = useState({
    serviceCategories: [initialCategory],
    effectiveDomain: initialFilters?.effectiveDomain || storedFilters?.effectiveDomain || "all_domains",
    serviceSubcategory: initialFilters?.serviceSubcategory || storedFilters?.serviceSubcategory || "all_subcategories",
    distance: storedFilters?.distance || "any_distance",
    date: storedFilters?.date ? new Date(storedFilters.date) : undefined as Date | undefined,
    timeSlot: storedFilters?.timeSlot || "any_time",
    sessionType: storedFilters?.sessionType || ['virtual', 'in_person'],
    showFreeSession: storedFilters?.showFreeSession || false,
    includeThirdPartyRating: storedFilters?.includeThirdPartyRating ?? true,
    genders: initialFilters?.genders || storedFilters?.genders || ['male', 'female', 'other'],
    language: initialFilters?.language || storedFilters?.language || 'all',
    issueCategory: storedFilters?.issueCategory || 'all_categories',
  });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Save filters to localStorage when they change (but not on initial load from ServiceSelection)
  useEffect(() => {
    // Don't save if we're using initialFilters (coming from ServiceSelection)
    if (initialFilters) return;
    
    // Don't save default values
    const isDefault = 
      filters.serviceCategories[0] === 'all_types' &&
      filters.effectiveDomain === 'all_domains' &&
      filters.serviceSubcategory === 'all_subcategories' &&
      filters.distance === 'any_distance' &&
      !filters.date &&
      filters.timeSlot === 'any_time' &&
      Array.isArray(filters.sessionType) && filters.sessionType.length === 2 &&
      filters.sessionType.includes('virtual') && filters.sessionType.includes('in_person') &&
      !filters.showFreeSession &&
      Array.isArray(filters.genders) && filters.genders.length === 3 &&
      filters.genders.includes('male') && filters.genders.includes('female') && filters.genders.includes('other') &&
      filters.language === 'all' &&
      filters.issueCategory === 'all_categories';
    
    if (isDefault) {
      localStorage.removeItem(FILTERS_STORAGE_KEY);
      return;
    }
    
    try {
      const toStore = {
        filters: {
          ...filters,
          date: filters.date?.toISOString()
        },
        timestamp: Date.now()
      };
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toStore));
      console.log('💾 Saved filters to localStorage');
    } catch (error) {
      console.error('Error saving filters:', error);
    }
  }, [filters, initialFilters]);

  // Define available time slots - hourly from 09:00 to 21:00
  const timeSlots = [
    'any_time',
    '09:00',
    '10:00',
    '11:00',
    '12:00',
    '13:00',
    '14:00',
    '15:00',
    '16:00',
    '17:00',
    '18:00',
    '19:00',
    '20:00',
    '21:00'
  ];

  // Simplified single-select category change logic
  const handleCategoryChange = useCallback((categoryName: string) => {
    console.log("Category changed to:", categoryName);
    setFilters(prev => ({ 
      ...prev, 
      serviceCategories: [categoryName] 
    }));
  }, []);

  const isDateDisabled = useCallback((date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  }, []);

  return {
    filters,
    setFilters,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    timeSlots,
    handleCategoryChange,
    isDateDisabled
  };
};
